import { requireEventbriteEnabled } from "./eventbriteFeature";
import { supabase } from "../lib/supabaseClient";

export const eventbriteRequest = async (body, signal) => {
  requireEventbriteEnabled();
  if (!supabase) throw new Error("Supabase is not configured.");
  const { data, error } = await supabase.functions.invoke("eventbrite", { body, signal });
  if (error) {
    let message = error.message;
    try { message = (await error.context?.clone?.().json())?.error || message; } catch { /* Network error. */ }
    throw new Error(message || "Could not connect to Eventbrite.");
  }
  return data;
};

export const loadAllEventbritePages = async (body, { signal, onProgress } = {}) => {
  const items = new Map();
  const seenPages = new Set();
  let cursor = {};
  for (let pageCount = 0; pageCount < 10000; pageCount += 1) {
    if (signal?.aborted) throw new Error("Import cancelled.");
    const response = await eventbriteRequest({ ...body, ...cursor }, signal);
    if (!Array.isArray(response?.items)) throw new Error("Eventbrite returned an invalid response.");
    response.items.forEach((item) => {
      if (!item || typeof item.id !== "string" || !item.id) throw new Error("Eventbrite returned an invalid record.");
      items.set(item.id, item);
    });
    onProgress?.(items.size);
    const pagination = response.pagination || {};
    const hasMore = pagination.has_more_items ?? (pagination.page_number < pagination.page_count);
    if (!hasMore) return [...items.values()];
    if (pagination.continuation) cursor = { continuation: pagination.continuation };
    else if (Number.isInteger(pagination.page_number)) cursor = { page: pagination.page_number + 1 };
    else throw new Error("Eventbrite did not return the next page. Please try again.");
    const key = JSON.stringify(cursor);
    if (seenPages.has(key)) throw new Error("Eventbrite returned a repeated page. Please try again.");
    seenPages.add(key);
  }
  throw new Error("This event is too large to import. Please use a spreadsheet export.");
};

// Open synchronously from a click to avoid popup blockers, without navigating away from unsaved work.
export const connectEventbrite = (signal) => {
  try { requireEventbriteEnabled(); } catch (error) { return Promise.reject(error); }
  const popup = window.open("about:blank", "badgeflow-eventbrite", "width=640,height=760");
  if (!popup) return Promise.reject(new Error("Allow popups for BadgeFlow, then connect again."));
  return new Promise((resolve, reject) => {
    let expectedState;
    let exchanging = false;
    let finished = false;
    const finish = (error) => {
      if (finished) return;
      finished = true;
      clearInterval(timer);
      clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      signal?.removeEventListener("abort", onAbort);
      popup.close();
      if (error) reject(error); else resolve();
    };
    const onAbort = () => finish(new Error("Connection cancelled."));
    const onMessage = async (event) => {
      if (event.origin !== window.location.origin || event.source !== popup ||
          event.data?.type !== "badgeflow-eventbrite-callback" || !expectedState ||
          event.data.state !== expectedState || exchanging) return;
      if (event.data.error) return finish(new Error("Eventbrite connection was not authorized. Please try again."));
      exchanging = true;
      try {
        await eventbriteRequest({ action: "exchange", code: event.data.code, state: expectedState }, signal);
        finish();
      } catch (error) { finish(error); }
    };
    const timer = setInterval(() => { if (popup.closed && !exchanging) finish(new Error("Eventbrite connection was cancelled.")); }, 500);
    const timeout = setTimeout(() => finish(new Error("Eventbrite connection timed out. Please try again.")), 5 * 60 * 1000);
    window.addEventListener("message", onMessage);
    signal?.addEventListener("abort", onAbort, { once: true });
    if (signal?.aborted) return onAbort();
    eventbriteRequest({ action: "connect" }, signal).then(({ url, state }) => {
      if (finished) return;
      const target = new URL(url);
      if (target.origin !== "https://www.eventbrite.com" || target.pathname !== "/oauth/authorize") throw new Error("Invalid Eventbrite authorization URL.");
      expectedState = state;
      popup.location.href = target.toString();
    }).catch(finish);
  });
};

export const hasEventbriteIdentity = (user) =>
  Boolean(user?.identities?.some((identity) => identity.provider === "custom:eventbrite"));

export const completeEventbriteSignIn = async (session) => {
  if (!hasEventbriteIdentity(session?.user)) throw new Error("Your Eventbrite sign-in could not be verified.");
  if (session.provider_token) {
    await eventbriteRequest({ action: "save-sign-in-token", token: session.provider_token });
    return;
  }
  // Supabase does not always retain provider tokens after a session refresh.
  const status = await eventbriteRequest({ action: "status" });
  if (!status.connected) throw new Error("Please sign in with Eventbrite again to finish connecting your events.");
};
